// The message contract between the Basil surface and the separately emitted verifier chunk
// (owner ruling D2: the verifier is a distinct artifact, identified before any write and asked for
// a verdict after every write). SHIP-SPEC-2026-09-05 section 3.2.
//
// This module holds types and exactly ONE runtime value. It sits on both sides of the boundary --
// the worker entry imports it, the client imports it, the write lane imports it -- so anything
// heavier here would be emitted into the worker chunk and the main bundle at the same time.
//
// Every message is structured-cloneable plain JSON. `before` and `after` are the serialized
// immutable raw reads; the client never hands over a mutable writer object.
import type {
  Fixture,
  RowCounts,
  Verdict,
  VerifiablePlan,
  VerifierProjection,
} from '../../dorian-shared/types/episodeScript';

/**
 * Bumped whenever a request or a reply field changes meaning. A reply that disagrees is refused.
 *
 * The `canon` exchange added for the staleness baseline (spec section 5.3) did NOT bump it: no
 * existing request or reply field changed meaning, and a chunk that predates `canon` already
 * answers an unrecognised kind with `kind: 'error'` (`workerHandler.ts`), which the client reads as
 * "no canon" and the staleness guard turns into a refusal. Old chunk, new client: fail closed, by
 * the contract that was already there. The artifact identity strings are unchanged for the same
 * reason — the projection and canon schema this build implements is the same one.
 */
export const BASIL_VERIFY_PROTOCOL = 1;

/** The literal type of the protocol constant, so a mistyped message is a compile error. */
export type BasilVerifyProtocol = typeof BASIL_VERIFY_PROTOCOL;

/** The projection and canon schema this build of the verifier implements. */
export const BASIL_PROJECTION_VERSION = 'v1+v2+layout/2026-09-05';

/** The chunk name pinned at the one construction site; recorded in every audit entry. */
export const BASIL_VERIFY_CHUNK_NAME = 'basilVerify';

/**
 * Who answered. The write lane records this in the audit entry before it sends anything, which is
 * what makes "a verifier artifact existed and was asked" auditable after the fact.
 */
export interface VerifierArtifactIdentity {
  /** protocol version, echoed so a stale chunk is detectable */
  protocol: BasilVerifyProtocol;
  /** the emitted chunk's own name plus the client release; recorded in every audit entry */
  buildId: string;
  /** the projection/canon schema this artifact implements */
  projectionVersion: string;
}

export interface VerifyHandshakeRequest {
  id: string;
  protocol: BasilVerifyProtocol;
  kind: 'handshake';
}

export interface VerifyVerdictRequest {
  id: string;
  protocol: BasilVerifyProtocol;
  kind: 'verify';
  before: Fixture;
  after: Fixture;
  plan: VerifiablePlan;
  /**
   * The engine's returned counts, or null only in whole-branch mode. Never plan.expectedCounts --
   * that verifies the plan against itself (spec section 3.2 rule 3).
   */
  actualCounts: RowCounts | null;
}

/**
 * The verifier's own per-node canon lines and digests, in the three canons the verdict compares:
 * the V1 content canon, the V2 whole-writable canon and the layout canon.
 *
 * The write lane's staleness baseline is these lines (spec section 5.3): the line
 * `` `${id}:${stepIds}:${djb2(canon(branch))}` `` per node, plus the projection digest. They are
 * asked for over this boundary rather than computed on the main thread, so the verifier's
 * canonicalisation stays in the separately emitted chunk (ruling D2, gate check G8).
 */
export interface VerifierCanonSet {
  v1: VerifierProjection;
  v2: VerifierProjection;
  layout: VerifierProjection;
}

export interface VerifyCanonRequest {
  id: string;
  protocol: BasilVerifyProtocol;
  kind: 'canon';
  /** The serialized immutable read to canonicalise. Never an alias to a mutable writer object. */
  fixture: Fixture;
}

export type VerifyWorkerRequest = VerifyHandshakeRequest | VerifyVerdictRequest | VerifyCanonRequest;

export interface VerifyHandshakeReply {
  id: string;
  protocol: BasilVerifyProtocol;
  kind: 'handshake';
  artifact: VerifierArtifactIdentity;
}

export interface VerifyVerdictReply {
  id: string;
  protocol: BasilVerifyProtocol;
  kind: 'verdict';
  artifact: VerifierArtifactIdentity;
  verdict: Verdict;
}

export interface VerifyErrorReply {
  id: string;
  protocol: BasilVerifyProtocol;
  kind: 'error';
  artifact: VerifierArtifactIdentity | null;
  message: string;
}

export interface VerifyCanonReply {
  id: string;
  protocol: BasilVerifyProtocol;
  kind: 'canon';
  artifact: VerifierArtifactIdentity;
  canon: VerifierCanonSet;
}

export type VerifyWorkerReply =
  | VerifyHandshakeReply
  | VerifyVerdictReply
  | VerifyCanonReply
  | VerifyErrorReply;

/**
 * The narrow view of a worker the client uses. A DOM `Worker` satisfies it structurally, which is
 * what lets `createVerifyWorker` return one without a cast, and a plain object satisfies it too,
 * which is what lets every test inject a fake instead of importing the construction site.
 */
export interface VerifyWorkerLike {
  postMessage(message: VerifyWorkerRequest): void;
  terminate(): void;
  onmessage: ((event: MessageEvent) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
  onmessageerror?: ((event: MessageEvent) => void) | null;
}

/** The factory shape the client takes by injection. `null` means "no verifier artifact". */
export type VerifyWorkerFactory = () => VerifyWorkerLike | null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** True when `value` is an artifact identity this protocol version can accept. */
export function isVerifierArtifactIdentity(value: unknown): value is VerifierArtifactIdentity {
  if (!isRecord(value)) return false;
  return value.protocol === BASIL_VERIFY_PROTOCOL
    && typeof value.buildId === 'string'
    && typeof value.projectionVersion === 'string';
}

function isProjection(value: unknown): value is VerifierProjection {
  if (!isRecord(value)) return false;
  return Array.isArray(value.lines) && Array.isArray(value.digest);
}

/** True when `value` carries all three projections a canon reply must have. */
export function isVerifierCanonSet(value: unknown): value is VerifierCanonSet {
  if (!isRecord(value)) return false;
  return isProjection(value.v1) && isProjection(value.v2) && isProjection(value.layout);
}

/**
 * True when `value` is a reply of this protocol version. The client checks this before it looks at
 * `id`: a reply from a stale chunk is refused rather than half-read.
 */
export function isVerifyWorkerReply(value: unknown): value is VerifyWorkerReply {
  if (!isRecord(value)) return false;
  if (value.protocol !== BASIL_VERIFY_PROTOCOL || typeof value.id !== 'string') return false;
  if (value.kind === 'handshake') return isVerifierArtifactIdentity(value.artifact);
  if (value.kind === 'verdict') return isVerifierArtifactIdentity(value.artifact) && isRecord(value.verdict);
  if (value.kind === 'canon') return isVerifierArtifactIdentity(value.artifact) && isVerifierCanonSet(value.canon);
  if (value.kind === 'error') return typeof value.message === 'string';
  return false;
}
